/**
 * SA-1092 §2: the page's one read of the shape rule — strict on loaded
 * stores (the manifest, the mailbox list and, since SA-1104, the personas),
 * so no control appears and then vanishes. A chat source is a persona with a
 * WhatsApp config (the New Conversation dialog's own list) or a chat thread
 * in the stats; a desk is email ingestion with a router mailbox.
 */
import { useMemo } from 'react'
import { chatThreadCount, inboxShape, type InboxShape } from '@/components/inbox/inboxShape'
import { routerMailboxes } from '@/components/inbox/filterChips'
import { useInboxFilterChipsState } from '@/hooks/useInboxFilterChips'
import { useFeatureStore } from '@/stores/featureStore'
import { useInboxStore } from '@/stores/inboxStore'
import { usePersonaStore } from '@/stores/personaStore'
import type { InboxViewTab } from '@/types/inbox'

/** `context` is the page's own view tab and channel chip (the inbox store's
 * values, handed in so the page and the rule read one source). */
export function useInboxShape(context: { viewTab: InboxViewTab; channelFilter: string | null }): InboxShape {
  const { viewTab, channelFilter } = context
  const isLoaded = useFeatureStore((s) => s.isLoaded)
  const features = useFeatureStore((s) => s.features)
  const personas = usePersonaStore((s) => s.personas)
  const personasLoaded = usePersonaStore((s) => s.isLoaded)
  // A chat thread in the Inbox is a chat source too: a WhatsApp, web or widget
  // thread, read from the stats the page loads, 0 until they arrive (SA-1380).
  const breakdown = useInboxStore((s) => s.stats?.channelBreakdown)
  const chatThreads = chatThreadCount(breakdown)
  const emailEnabled = isLoaded && features.has('email_ingestion')
  const { mailboxes, loaded: mailboxesLoaded } = useInboxFilterChipsState(emailEnabled)
  return useMemo(() => inboxShape({
    features: (flag) => features.has(flag),
    routerMailboxes: mailboxes ? routerMailboxes(mailboxes).length : 0,
    chatPersonas: Object.values(personas).filter((p) => p.whatsappConfig?.enabled).length,
    chatThreads,
    loaded: isLoaded && mailboxesLoaded && personasLoaded,
  }, { viewTab, channelFilter }), [features, personas, chatThreads, mailboxes, isLoaded, mailboxesLoaded, personasLoaded, viewTab, channelFilter])
}
